import { Col, Row, message, Button, Space, Popconfirm, Tooltip } from "antd";
import { CursorTable } from "components/cursorTable";
import {
  useFetchEligibleDebtorsForGroupQuery,
  useDeleteEligibleDebtorsForGroupMutation,
  useMergeEligibleDebtorsMutation,
} from "features/mergeGroups/mergeDebtorsAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import { formatDate } from "common/utils";
import { MergeDebtorsBulkActions } from "features/mergeGroups/components/mergeDebtorsBulkActions";
import {
  MergeCellsOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { DATE_TIME_FORMAT } from "common/constants";
import moment from "moment-timezone";

const StyledTable = styled(CursorTable)`
  & .ant-checkbox-input {
    pointer-events: auto;
  }

  & .ant-table-selection-column {
    pointer-events: none;
  }

  & .ant-table-container {
    max-height: calc(100vh - 240px);
    overflow: scroll;
  }

  & .ant-table-cell {
    padding: 4px 8px !important;
  }
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: red;
  margin-left: 4px;
`;

const StyledLinkContainer = styled.div`
  margin-right: 8px;
  display: inline-block;
`;

const StyledCol = styled(Col)`
  overflow: auto;
`;

const StyledActionContainer = styled(Row)`
  background: #fff;
  height: 48px;
  margin-top: 1px;
  border-left: 1px solid #f0f0f0;
  align-items: center;
  flex: 1;
  min-width: 200px;
  display: flex;
  justify-content: space-between;
`;

const StyledActions = styled(Row)`
  margin-left: 47px;
  height: 100%;
  border-left: 1px solid #f0f0f0;
  align-items: center;
  justify-content: space-between;
`;

function MergeDebtorsTable({ queryParams, mergeGroups }) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [virtualPage, setVirtualPage] = useState(1);
  const [pagination, setPagination] = useState({
    pageSize: 100,
    prevToken: null,
    nextToken: null,
  });

  const mergeGroupIdToNameMap = useMemo(() => {
    if (!mergeGroups) return {};
    return mergeGroups.reduce((acc, group) => {
      acc[group.id] = group.groupName;
      return acc;
    }, {});
  }, [mergeGroups]);

  const { data, isLoading, isFetching } = useFetchEligibleDebtorsForGroupQuery(
    {
      ...queryParams,
      virtualPage,
      ...pagination,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // Action hooks
  const [deleteEligibleDebtors, { isLoading: isDeleting }] =
    useDeleteEligibleDebtorsForGroupMutation();
  const [mergeDebtors, { isLoading: isMerging }] = useMergeEligibleDebtorsMutation();

  const {
    data: {
      [PERMISSIONS.DEBTOR_MERGE_GROUP__DELETE]: isMergeGroupDeleteAuthorized,
      [PERMISSIONS.DEBTOR_MERGE_GROUP__UPDATE]: isMergeGroupUpdateAuthorized,
    },
  } = useAuthorizations();

  // Pagination handlers
  const handleSetVirtualPage = (newVirtualPage) => {
    setVirtualPage(newVirtualPage);
  };

  const handleTableChange = (newPagination, _, sorter) => {
    if (newPagination && Object.keys(newPagination).length > 0) {
      setVirtualPage(newPagination.virtualPage);
      setPagination({
        pageSize: newPagination.pageSize,
        prevToken: newPagination.prevToken,
        nextToken: newPagination.nextToken,
      });
    }
  };

  // Action handlers
  const handleMerge = useCallback(
    async (record) => {
      const result = await mergeDebtors({
        mergeEligibleDebtorsId: record.id,
      });

      if ("error" in result) {
        message.error("Failed to merge debtors");
      }
      if ("data" in result) {
        message.success("Debtors successfully merged");
      }
    },
    [mergeDebtors],
  );

  const handleDelete = useCallback(
    async (record) => {
      const result = await deleteEligibleDebtors({
        mergeEligibleDebtorsId: record.id,
      });

      if ("error" in result) {
        message.error("Failed to delete match");
      }
      if ("data" in result) {
        message.success("Match successfully deleted");
      }
    },
    [deleteEligibleDebtors],
  );

  const rowSelection = {
    onChange: (selectedRowKeys, newSelectedRows) => {
      setSelectedRows(newSelectedRows);
    },
  };
  // Update table columns with the lookup maps
  const tableColumnsWithData = useMemo(
    () => [
      {
        title: "Parent Debtor",
        dataIndex: "parentDebtorId",
        key: "parentDebtorId",
        width: 200,
        render: (text, record) => {
          return (
            <StyledLinkContainer>
              <a target="_blank" href={`/debtors/${record.parentDebtorId}`} rel="noreferrer">
                {record.parentDebtor.name} ({record.parentDebtorId})
              </a>
            </StyledLinkContainer>
          );
        },
      },
      {
        title: "Child Debtor(s)",
        dataIndex: "childrenDebtorIds",
        key: "childrenDebtorIds",
        width: 200,
        render: (text, record) => {
          return (
            <Row>
              {record.childrenDebtors.map((codebtor) => (
                <StyledLinkContainer key={codebtor.id}>
                  <a target="_blank" href={`/debtors/${codebtor.id}`} rel="noreferrer">
                    {codebtor.name} ({codebtor.id})
                  </a>
                </StyledLinkContainer>
              ))}
            </Row>
          );
        },
      },
      {
        title: "Merge Group",
        dataIndex: "mergeGroupId",
        key: "mergeGroupId",
        width: 150,
        render: (text) => {
          if (!text) return "-";
          const groupName = mergeGroupIdToNameMap[text];
          return groupName ?? text;
        },
      },
      {
        title: "Match Date",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 100,
        render: (text, record) => (record.createdAt ? formatDate(record.createdAt) : "-"),
      },
      {
        title: "Updated At",
        dataIndex: "updatedAt",
        key: "updatedAt",
        render: (text, _) => (text ? moment(text).format(DATE_TIME_FORMAT) : "-"),
        width: 150,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (text, record) => {
          if (record.errorMessage) {
            return (
              <Tooltip title={record.errorMessage}>
                Merge Failed
                <span>
                  <StyledInfoCircleOutlined />
                </span>
              </Tooltip>
            );
          }
          if (record.asyncJob?.status === "error") {
            return (
              <Tooltip title={record.asyncJob.errorMessage} trigger="hover">
                Merge Failed
                <span>
                  <StyledInfoCircleOutlined />
                </span>
              </Tooltip>
            );
          }
          if (record.asyncJob && record.asyncJob.status === "in_progress") {
            return "Merge In Progress";
          }
          return "";
        },
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        align: "center",
        width: 100,
        dataIndex: "actions",
        render: (text, record) => (
          <Space size="small">
            {isMergeGroupUpdateAuthorized && (
              <Popconfirm
                title="Are you sure you want to merge these debtors?"
                description="Merging is irreversible and cannot be undone."
                onConfirm={() => handleMerge(record)}
                okText="Yes"
                cancelText="No"
                icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<MergeCellsOutlined />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  title="Merge Debtors"
                />
              </Popconfirm>
            )}
            {isMergeGroupDeleteAuthorized && (
              <Popconfirm
                title="Are you sure you want to delete this match?"
                description="Deleted matches cannot be recovered."
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
                icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  title="Delete Match"
                />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [
      handleDelete,
      handleMerge,
      isMergeGroupDeleteAuthorized,
      isMergeGroupUpdateAuthorized,
      mergeGroupIdToNameMap,
    ],
  );

  return (
    <StyledCol>
      <StyledActionContainer>
        <StyledActions>
          <MergeDebtorsBulkActions dataSource={selectedRows} />
        </StyledActions>
      </StyledActionContainer>
      <StyledTable
        bordered
        sticky
        scroll={{ x: "max-content" }}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        columns={tableColumnsWithData}
        dataSource={data?.results?.filter(
          (result) => !result.asyncJob || result.asyncJob?.status === "error",
        )}
        loading={isLoading || isDeleting || isMerging || isFetching}
        rowKey="id"
        locale={{
          emptyText: "No pending merge debtors found",
        }}
        pageSize={data?.meta?.pageSize || 100}
        prevToken={data?.meta?.prevToken}
        nextToken={data?.meta?.nextToken}
        resultCount={data?.meta?.resultCount}
        virtualPage={virtualPage}
        setVirtualPage={handleSetVirtualPage}
        onChange={handleTableChange}
      />
    </StyledCol>
  );
}

export default MergeDebtorsTable;
